import React from 'react';
import { Card } from '@/shared/components/ui/Card';

const SkeletonPulse: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

export const MorningSummarySkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header skeleton */}
      <div>
        <SkeletonPulse className="h-4 w-48 mb-4" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SkeletonPulse className="h-12 w-12 rounded-lg" />
            <div>
              <SkeletonPulse className="h-6 w-40 mb-2" />
              <SkeletonPulse className="h-4 w-64" />
            </div>
          </div>
          <div className="flex gap-2">
            <SkeletonPulse className="h-9 w-24" />
            <SkeletonPulse className="h-9 w-32" />
          </div>
        </div>
      </div>

      {/* Quick actions skeleton */}
      <div className="flex gap-3">
        {[1, 2, 3, 4].map((i) => (
          <SkeletonPulse key={i} className="h-10 w-32 rounded-lg" />
        ))}
      </div>

      {/* Stats grid skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <SkeletonPulse className="h-4 w-20" />
                <SkeletonPulse className="h-8 w-12" />
                <SkeletonPulse className="h-3 w-16" />
              </div>
              <SkeletonPulse className="h-9 w-9 rounded-lg" />
            </div>
          </Card>
        ))}
      </div>

      {/* Main content skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left card */}
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <SkeletonPulse className="h-5 w-5 rounded" />
              <SkeletonPulse className="h-5 w-40" />
            </div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-3 border border-gray-100 rounded-lg">
                  <SkeletonPulse className="h-4 w-32 mb-2" />
                  <SkeletonPulse className="h-3 w-48" />
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Right card */}
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <SkeletonPulse className="h-5 w-5 rounded" />
              <SkeletonPulse className="h-5 w-36" />
            </div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-3 border border-gray-100 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <SkeletonPulse className="h-5 w-16 rounded" />
                    <SkeletonPulse className="h-4 w-24" />
                  </div>
                  <SkeletonPulse className="h-3 w-full" />
                  <SkeletonPulse className="h-3 w-20 mt-1" />
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
